import { useMutation, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../store";
import { companyActions } from "../../store/company-slice";
import {
  copyReplacementBlocks,
  fetchMailTemplateChangeHistory,
  fetchMailTemplateSummary,
  postMailTemplate,
} from "../../utils/http/mail-templates";
import { queryClient } from "../../utils/http/queryClient";

function Explanation() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const { language, event, automation } = params;

  const [nameOfBlock, setNameOfBlock] = useState("");

  const includeDrafts = useAppSelector((state) => state.company.mailTemplateChangeHistoryInclDrafts);

  // Create a block
  // TODO: We should check if the block already exists
  const { mutate: mutateSaveToDraft } = useMutation({
    mutationFn: postMailTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      navigate(`/mail-templates/${language}/${event}/blocks/${nameOfBlock}/draft`);
    },
  });

  const { data: summary } = useQuery({
    queryKey: ["mailTemplates", "summary"],
    queryFn: ({ signal }) => fetchMailTemplateSummary({ signal }),
    staleTime: 2000,
  });

  const { mutate: mutateCopyReplacementBlocks, isPending: isCopyingReplacementBlocks } = useMutation({
    mutationFn: copyReplacementBlocks,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
    },
  });

  const hasNoReplacementBlocks =
    !!summary && !summary.maintained.some((t: any) => t.event === "replacements");
  const isAtLanguageRoot = !!language && !automation && !event;

  const {
    data: changeHistory,
    refetch,
  } = useQuery({
    queryKey: ["changeHistory"],
    queryFn: ({ signal }) => fetchMailTemplateChangeHistory({ signal }),
    staleTime: 2000,
  });

  const onShowDrafts = (e: any) => {
    e.preventDefault();
    dispatch(companyActions.toggleMailTemplateChangeHistoryInclDrafts());
    refetch();
  };

  if (event === "replacements") {
    return (
      <>
        <h2>Create a new global block</h2>
        <p>
          Global blocks can be used in all the templates below. You can include some conditionals, as well as use other
          blocks or fields.
        </p>
        <Container className="pt-4">
          <Row>
            <Col sm="3" />
            <Col sm="6">
              <Form>
                <Form.Group as={Row} className="mb-3">
                  <Row className="align-items-center">
                    <Col sm="8">
                      <Form.Control
                        plaintext
                        style={{ border: "solid #999999", padding: "10px" }}
                        placeholder="Name of your block"
                        onChange={(e) => setNameOfBlock(e.target.value)}
                        value={nameOfBlock}
                      />
                    </Col>
                    <Col sm="4">
                      <Button
                        onClick={() => {
                          mutateSaveToDraft({
                            mailTemplatePath: {
                              language,
                              event,
                              action: "blocks",
                              target: nameOfBlock,
                              version: "draft",
                            },
                            subject: "",
                            body: "",
                          });
                        }}
                      >
                        <i className="bi bi-floppy-fill"></i> Create
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <div>
      Please drill down further.
      <br />
      <br />
      {isAtLanguageRoot && hasNoReplacementBlocks && (
        <div className="mb-4">
          <Button onClick={() => mutateCopyReplacementBlocks()} disabled={isCopyingReplacementBlocks}>
            {isCopyingReplacementBlocks ? "Copying..." : "Copy replacement blocks from template"}
          </Button>
        </div>
      )}
      <h2>Mail Template Change History</h2>
      {changeHistory?.map((line: any, index: number) => {
        let text = "";

        let promotedOrSaved = "promoted";
        if (line.version == "draft") {
          promotedOrSaved = "saved";
        }

        if (line.promoted_by) {
          const indexOfAt = line?.promoted_by.indexOf("@");
          const promotedBy = indexOfAt !== -1 ? line?.promoted_by.substring(0, indexOfAt) : line?.promoted_by;
          text += promotedBy.charAt(0).toUpperCase() + promotedBy.slice(1) + ` ${promotedOrSaved} `;
        } else {
          text += `Somebody ${promotedOrSaved} `;
        }

        text += `${line.event} > ${line.action} > ${line.target}`;

        if (line.promoted_on) {
          let promotedOn = DateTime.fromISO(line.promoted_on, { zone: "utc" }).setZone("Europe/Paris");
          if (
            promotedOn >=
            DateTime.fromObject({
              year: 2025,
              month: 5,
              day: 1,
            })
          ) {
            text += ` on ${promotedOn.toFormat("dd/MM/yyyy HH:mm:ss ZZZZ")}`;
          }
        }

        text += ` (version ${line.version})`;

        return (
          <Row key={`${line.event}-${line.action}-${line.target}-${line.version}-${index}`}>
            <Col>{text}</Col>
          </Row>
        );
      })}
      <br />
      <a href="#" onClick={onShowDrafts}>
        {includeDrafts && <>exclude drafts</>}
        {!includeDrafts && <>include drafts</>}
      </a>
    </div>
  );
}

export default Explanation;
